import {
  LEVELS,
  LEVEL_LABELS,
  type PermissionExceptionDto,
  type PermissionFeatureDto,
  type PermissionLevel,
  type PersonPermissionsDto,
  type Resource,
  type RoleDto,
} from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import { useConfirm } from './ConfirmDialog';
import { OrgUnitSelect } from './OrgUnitPicker';
import { PersonSelect } from './PersonPicker';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  SectionTitle,
  Select,
  cn,
  useToast,
} from './ui';

/*
 * Zugriffsrechte in drei Sichten:
 * - Funktionen: welche Stufe hat jede Funktion je Recht (Matrix)
 * - Zugriffsrechte: alle Personen-Ausnahmen und "Rechte prüfen"
 * - Personenseite: Rechte genau einer Person, dort auch Ausnahmen setzen
 */

const LEVEL_STYLE: Record<PermissionLevel, string> = {
  none: 'text-slate-300',
  read: 'bg-sky-50 text-sky-700 ring-sky-200',
  edit: 'bg-brand-50 text-brand-700 ring-brand-200',
  manage: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
};

export function LevelBadge({ level, title }: { level: PermissionLevel; title?: string }): JSX.Element {
  if (level === 'none') {
    return <span className={LEVEL_STYLE.none} title={title ?? LEVEL_LABELS.none}>–</span>;
  }
  return (
    <span
      title={title}
      className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset', LEVEL_STYLE[level])}
    >
      {LEVEL_LABELS[level]}
    </span>
  );
}

function FeatureName({ feature }: { feature: PermissionFeatureDto }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1" title={feature.description ?? undefined}>
      {feature.confidential && (
        <span aria-label="vertraulich" title="Vertraulich – wird nur bewusst vergeben">🔒</span>
      )}
      {feature.label}
    </span>
  );
}

function usePermissionFeatures() {
  return useQuery({
    queryKey: ['permissions', 'features'],
    queryFn: () => api.get<PermissionFeatureDto[]>('/api/permissions/features'),
  });
}

function groupFeatures(features: PermissionFeatureDto[]): { group: string; features: PermissionFeatureDto[] }[] {
  const groups: { group: string; features: PermissionFeatureDto[] }[] = [];
  for (const feature of features) {
    const last = groups.at(-1);
    if (last?.group === feature.group) last.features.push(feature);
    else groups.push({ group: feature.group, features: [feature] });
  }
  return groups;
}

const errorText = (err: unknown, fallback: string): string =>
  err instanceof ApiError ? err.message : fallback;

/** Datum aus einem Datumsfeld als Ende des Tages. */
function endOfDay(date: string): string | null {
  return date ? new Date(`${date}T23:59:59`).toISOString() : null;
}

/* ---------------------------------------------------------------- Funktionen */

export function RolesTab(): JSX.Element {
  const { can } = useAuth();
  const mayManage = can('permissions', 'manage');
  const queryClient = useQueryClient();
  const toast = useToast();

  const features = usePermissionFeatures();
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<RoleDto[]>('/api/roles') });

  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState<RoleDto | null>(null);
  const [editFeature, setEditFeature] = useState<PermissionFeatureDto | null>(null);

  const setLevel = useMutation({
    mutationFn: (input: { roleId: string; feature: Resource; level: PermissionLevel }) =>
      api.put<RoleDto & { affectedPersons?: number }>(`/api/roles/${input.roleId}/levels/${input.feature}`, { level: input.level }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
      const betroffen = typeof result?.affectedPersons === 'number' ? result.affectedPersons : null;
      toast.show(
        betroffen === null
          ? 'Recht gespeichert'
          : `Recht gespeichert – ${betroffen} ${betroffen === 1 ? 'Person ist' : 'Personen sind'} betroffen`,
      );
    },
    onError: (err: unknown) => toast.show(errorText(err, 'Speichern fehlgeschlagen'), 'error'),
  });

  if (features.isLoading || roles.isLoading) return <LoadingBlock />;
  if (features.error) return <ErrorBlock error={features.error} />;
  if (roles.error) return <ErrorBlock error={roles.error} />;

  const visibleRoles = (roles.data ?? [])
    .filter((role) => showArchived || !role.archived)
    .sort((a, b) =>
      Number(b.isAdmin) - Number(a.isAdmin)
      || Number(b.isSystem) - Number(a.isSystem)
      || a.name.localeCompare(b.name, 'de'));
  const hasArchived = (roles.data ?? []).some((role) => role.archived);

  return (
    <div className="space-y-4">
      <Card>
        <CardBody className="space-y-3">
          <SectionTitle
            title="Funktionen und ihre Rechte"
            subtitle="Jede Funktion hat je Recht eine Stufe. Sie gilt im zugeordneten Bereich und – wenn gewünscht – in dessen Unterbereichen."
            action={mayManage ? <Button size="sm" onClick={() => setCreateOpen(true)}>+ Funktion</Button> : undefined}
          />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><LevelBadge level="read" /> nur lesen</span>
            <span className="inline-flex items-center gap-1"><LevelBadge level="edit" /> anlegen und ändern</span>
            <span className="inline-flex items-center gap-1"><LevelBadge level="manage" /> auch löschen</span>
            <span>🔒 vertraulich</span>
            {hasArchived && (
              <Checkbox label="Archivierte Funktionen zeigen" checked={showArchived} onChange={setShowArchived} />
            )}
          </div>
          <p className="text-xs text-slate-500">
            Standardfunktionen werden zentral gepflegt. Für abweichende Rechte eine eigene Funktion anlegen
            (z. B. als Kopie) oder bei einzelnen Personen eine Ausnahme setzen.
          </p>
        </CardBody>
      </Card>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left align-bottom">
              <th className="sticky left-0 z-10 min-w-[12rem] bg-white px-3 py-2 text-xs font-semibold uppercase text-slate-400">
                Recht
              </th>
              {visibleRoles.map((role) => (
                <th key={role.id} className="min-w-[7.5rem] px-2 py-2 text-xs font-medium text-slate-700">
                  <div className="flex flex-col gap-1">
                    <span className={cn('font-semibold', role.archived && 'text-slate-400 line-through')}>
                      {role.name}
                    </span>
                    <span className="flex flex-wrap gap-1">
                      {role.isAdmin ? (
                        <Badge tone="warning">Vollzugriff</Badge>
                      ) : role.isSystem ? (
                        <Badge tone="neutral">Standard</Badge>
                      ) : (
                        <Badge tone="info">Eigene</Badge>
                      )}
                      <span className="text-slate-400" title="Aktuelle Zuordnungen">{role.assignmentCount}×</span>
                    </span>
                    {mayManage && !role.isSystem && (
                      <button
                        type="button"
                        className="self-start text-xs text-brand-700 hover:underline"
                        onClick={() => setEditRole(role)}
                      >
                        bearbeiten
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupFeatures(features.data ?? []).map((group) => (
              <Fragment key={group.group}>
                <tr className="bg-slate-50">
                  <td
                    colSpan={visibleRoles.length + 1}
                    className="sticky left-0 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"
                  >
                    {group.group}
                  </td>
                </tr>
                {group.features.map((feature) => (
                  <tr key={feature.key} className="border-b border-slate-100">
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium text-slate-700">
                      {mayManage ? (
                        <button type="button" className="text-left hover:underline" onClick={() => setEditFeature(feature)}>
                          <FeatureName feature={feature} />
                        </button>
                      ) : (
                        <FeatureName feature={feature} />
                      )}
                    </td>
                    {visibleRoles.map((role) => {
                      const level: PermissionLevel = role.isAdmin ? 'manage' : (role.levels[feature.key] ?? 'none');
                      const editable = mayManage && !role.isSystem && !role.archived;
                      return (
                        <td key={role.id} className="px-2 py-1.5">
                          {editable ? (
                            <select
                              aria-label={`${feature.label} für ${role.name}`}
                              className={cn('rounded-md border-slate-200 py-1 pl-2 pr-7 text-xs', level === 'none' && 'text-slate-400')}
                              value={level}
                              disabled={setLevel.isPending}
                              onChange={(e) => setLevel.mutate({
                                roleId: role.id,
                                feature: feature.key,
                                level: e.target.value as PermissionLevel,
                              })}
                            >
                              {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
                            </select>
                          ) : (
                            <LevelBadge level={level} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <CreateRoleDialog open={createOpen} onClose={() => setCreateOpen(false)} roles={roles.data ?? []} />
      {editRole && <EditRoleDialog key={editRole.id} role={editRole} onClose={() => setEditRole(null)} />}
      {editFeature && (
        <EditFeatureDialog key={editFeature.key} feature={editFeature} onClose={() => setEditFeature(null)} />
      )}
    </div>
  );
}

function CreateRoleDialog({ open, onClose, roles }: { open: boolean; onClose: () => void; roles: RoleDto[] }): JSX.Element | null {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [copyFrom, setCopyFrom] = useState('');

  const create = useMutation({
    mutationFn: () => api.post<RoleDto>('/api/roles', {
      name,
      description: description || null,
      copyFromRoleId: copyFrom || null,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.show('Funktion angelegt');
      setName('');
      setDescription('');
      setCopyFrom('');
      onClose();
    },
    onError: (err: unknown) => toast.show(errorText(err, 'Anlegen fehlgeschlagen'), 'error'),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Neue Funktion"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!name.trim()}>Anlegen</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Platzwart" />}
        </Field>
        <Field label="Beschreibung">
          {(id) => <Input id={id} value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        <Field label="Rechte übernehmen von" hint="Die Rechte lassen sich danach in der Übersicht anpassen.">
          {(id) => (
            <Select id={id} value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
              <option value="">– ohne Rechte beginnen –</option>
              {roles.filter((role) => !role.isAdmin && !role.archived).map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function EditRoleDialog({ role, onClose }: { role: RoleDto; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');

  const save = useMutation({
    mutationFn: (patch: { name?: string; description?: string | null; archived?: boolean }) =>
      api.patch<RoleDto>(`/api/roles/${role.id}`, patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.show('Funktion gespeichert');
      onClose();
    },
    onError: (err: unknown) => toast.show(errorText(err, 'Speichern fehlgeschlagen'), 'error'),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${role.name} bearbeiten`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => save.mutate({ archived: !role.archived })}
            disabled={save.isPending || (!role.archived && role.assignmentCount > 0)}
          >
            {role.archived ? 'Wiederherstellen' : 'Archivieren'}
          </Button>
          <Button onClick={() => save.mutate({ name, description: description || null })} loading={save.isPending} disabled={!name.trim()}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" required>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Beschreibung">
          {(id) => <Input id={id} value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        {!role.archived && role.assignmentCount > 0 && (
          <p className="text-xs text-slate-500">
            Archivieren ist erst möglich, wenn die Funktion niemandem mehr zugeordnet ist ({role.assignmentCount} aktuell).
          </p>
        )}
      </div>
    </Dialog>
  );
}

function EditFeatureDialog({ feature, onClose }: { feature: PermissionFeatureDto; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [label, setLabel] = useState(feature.label);
  const [description, setDescription] = useState(feature.description ?? '');
  const [confidential, setConfidential] = useState(feature.confidential);

  const save = useMutation({
    mutationFn: () => api.patch(`/api/permissions/features/${feature.key}`, {
      label,
      description: description || null,
      confidential,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['permissions', 'features'] });
      toast.show('Recht gespeichert');
      onClose();
    },
    onError: (err: unknown) => toast.show(errorText(err, 'Speichern fehlgeschlagen'), 'error'),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Recht beschreiben"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!label.trim()}>Speichern</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Bezeichnung" required>
          {(id) => <Input id={id} value={label} onChange={(e) => setLabel(e.target.value)} />}
        </Field>
        <Field label="Beschreibung">
          {(id) => <Input id={id} value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        <Checkbox label="Vertraulich (hervorheben, nur bewusst vergeben)" checked={confidential} onChange={setConfidential} />
      </div>
    </Dialog>
  );
}

/* ----------------------------------------------------------- Zugriffsrechte */

export function AccessTab(): JSX.Element {
  const { can } = useAuth();
  const mayManage = can('permissions', 'manage');
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, confirmDialog } = useConfirm();

  const features = usePermissionFeatures();
  const exceptions = useQuery({
    queryKey: ['permissions', 'exceptions'],
    queryFn: () => api.get<PermissionExceptionDto[]>('/api/permissions/exceptions'),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [inspectPerson, setInspectPerson] = useState('');
  const [inspectUnit, setInspectUnit] = useState<string | null>(null);

  const effective = useQuery({
    queryKey: ['permissions', 'effective', inspectPerson, inspectUnit],
    queryFn: () =>
      api.get<{
        person: { id: string; name: string };
        permissions: { resource: Resource; level: PermissionLevel; reason: string }[];
      }>(`/api/permissions/effective/${inspectPerson}`, { organisationUnitId: inspectUnit ?? '' }),
    enabled: !!inspectPerson && !!inspectUnit,
  });

  const remove = useMutation({
    mutationFn: (entry: PermissionExceptionDto) => api.del(`/api/persons/${entry.personId}/permissions/${entry.feature}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['permissions'] });
      toast.show('Ausnahme entfernt');
    },
    onError: (err: unknown) => toast.show(errorText(err, 'Entfernen fehlgeschlagen'), 'error'),
  });

  if (exceptions.isLoading || features.isLoading) return <LoadingBlock />;
  if (exceptions.error) return <ErrorBlock error={exceptions.error} />;

  const featureByKey = new Map((features.data ?? []).map((f) => [f.key, f]));
  const now = Date.now();

  const ausnahmeEntfernen = async (entry: PermissionExceptionDto): Promise<void> => {
    const recht = featureByKey.get(entry.feature)?.label ?? entry.feature;
    const ok = await confirm({
      title: 'Ausnahme entfernen?',
      message: `Die Ausnahme für ${entry.personName} (${recht}) wird entfernt. ${entry.personName} hat dann wieder die Stufe aus den eigenen Funktionen.`,
      confirmLabel: 'Ja, entfernen',
      tone: 'danger',
    });
    if (ok) remove.mutate(entry);
  };

  return (
    <div className="space-y-4">
      {confirmDialog}
      <Card>
        <CardBody>
          <SectionTitle
            title="Ausnahmen für Personen"
            subtitle="Ersetzen für eine Person die Stufe aus ihren Funktionen – im gesamten Verein"
            action={mayManage ? <Button size="sm" onClick={() => setDialogOpen(true)}>+ Ausnahme</Button> : undefined}
          />
          {(exceptions.data ?? []).length === 0 ? (
            <p className="text-sm text-slate-500">
              Es sind keine Ausnahmen vergeben. Alle Zugriffe ergeben sich aus den Funktionen.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {(exceptions.data ?? []).map((entry) => {
                const feature = featureByKey.get(entry.feature);
                const expired = entry.validUntil !== null && Date.parse(entry.validUntil) < now;
                return (
                  <li key={entry.id} className="flex flex-wrap items-center gap-2 py-2.5">
                    <div className="min-w-0 flex-1">
                      <Link to={`/personen/${entry.personId}`} className="text-sm font-medium text-slate-800 hover:underline">
                        {entry.personName}
                      </Link>
                      <p className="text-xs text-slate-500">
                        {feature ? <FeatureName feature={feature} /> : entry.feature}
                        {entry.validUntil && ` · bis ${formatDate(entry.validUntil)}`}
                        {entry.note && ` · ${entry.note}`}
                        {entry.createdByName && ` · von ${entry.createdByName}`}
                      </p>
                    </div>
                    {expired && <Badge tone="neutral">abgelaufen</Badge>}
                    {entry.level === 'none' ? <Badge tone="danger">Entzogen</Badge> : <LevelBadge level={entry.level} />}
                    {mayManage && (
                      <button
                        type="button"
                        className="text-xs text-slate-400 hover:text-rose-600"
                        onClick={() => void ausnahmeEntfernen(entry)}
                      >
                        entfernen
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle title="Rechte prüfen" subtitle="Zeigt an, was eine Person in einem Bereich tatsächlich darf" />
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <Field label="Person">
              {(id) => (
                <PersonSelect id={id} value={inspectPerson || null} onChange={(v) => setInspectPerson(v ?? '')} />
              )}
            </Field>
            <Field label="Bereich">
              {(id) => <OrgUnitSelect id={id} value={inspectUnit} onChange={setInspectUnit} />}
            </Field>
          </div>
          {effective.data && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
                    <th className="py-2 pr-3">Recht</th>
                    <th className="px-2 py-2">Stufe</th>
                    <th className="px-2 py-2">Warum</th>
                  </tr>
                </thead>
                <tbody>
                  {effective.data.permissions.map((entry) => {
                    const feature = featureByKey.get(entry.resource);
                    return (
                      <tr key={entry.resource} className="border-b border-slate-100">
                        <td className="py-2 pr-3 font-medium text-slate-700">
                          {feature ? <FeatureName feature={feature} /> : entry.resource}
                        </td>
                        <td className="px-2 py-2"><LevelBadge level={entry.level} /></td>
                        <td className="px-2 py-2 text-xs text-slate-500">{entry.reason}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {dialogOpen && (
        <ExceptionDialog open onClose={() => setDialogOpen(false)} features={features.data ?? []} />
      )}
    </div>
  );
}

function ExceptionDialog({
  open,
  onClose,
  features,
  personId: fixedPersonId,
  feature: fixedFeature,
  current,
}: {
  open: boolean;
  onClose: () => void;
  features: PermissionFeatureDto[];
  personId?: string;
  feature?: Resource;
  current?: PermissionExceptionDto | null;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [personId, setPersonId] = useState(fixedPersonId ?? '');
  const [feature, setFeature] = useState<Resource | ''>(fixedFeature ?? '');
  const [level, setLevel] = useState<PermissionLevel>(current?.level ?? 'read');
  const [validUntil, setValidUntil] = useState(current?.validUntil?.slice(0, 10) ?? '');
  const [note, setNote] = useState(current?.note ?? '');

  const save = useMutation({
    mutationFn: () => api.put(`/api/persons/${fixedPersonId ?? personId}/permissions/${fixedFeature ?? feature}`, {
      level,
      validUntil: endOfDay(validUntil),
      note: note || null,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['permissions'] });
      await queryClient.invalidateQueries({ queryKey: ['person-permissions'] });
      toast.show('Ausnahme gespeichert');
      onClose();
    },
    onError: (err: unknown) => toast.show(errorText(err, 'Speichern fehlgeschlagen'), 'error'),
  });

  const selected = features.find((f) => f.key === (fixedFeature ?? feature));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={selected ? `Ausnahme: ${selected.label}` : 'Ausnahme vergeben'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!(fixedPersonId ?? personId) || !(fixedFeature ?? feature)}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!fixedPersonId && (
          <Field label="Person" required>
            {(id) => <PersonSelect id={id} value={personId || null} onChange={(v) => setPersonId(v ?? '')} />}
          </Field>
        )}
        {!fixedFeature && (
          <Field label="Recht" required>
            {(id) => (
              <Select id={id} value={feature} onChange={(e) => setFeature(e.target.value as Resource)}>
                <option value="">– bitte wählen –</option>
                {features.map((f) => (
                  <option key={f.key} value={f.key}>{f.confidential ? '🔒 ' : ''}{f.label}</option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label="Stufe" hint="Gilt im gesamten Verein und ersetzt die Stufe aus den Funktionen.">
          {(id) => (
            <Select id={id} value={level} onChange={(e) => setLevel(e.target.value as PermissionLevel)}>
              {LEVELS.map((l) => (
                <option key={l} value={l}>{l === 'none' ? 'Entziehen (kein Zugriff)' : LEVEL_LABELS[l]}</option>
              ))}
            </Select>
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Gültig bis" hint="Leer lassen für unbefristet">
            {(id) => <Input id={id} type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />}
          </Field>
          <Field label="Begründung">
            {(id) => <Input id={id} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------ Personenseite */

export function PersonPermissionsCard({ personId }: { personId: string }): JSX.Element | null {
  const { can } = useAuth();
  const mayRead = can('permissions', 'read');
  const mayManage = can('permissions', 'manage');
  const queryClient = useQueryClient();
  const toast = useToast();

  const { confirm, confirmDialog } = useConfirm();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{ feature: Resource; current: PermissionExceptionDto | null } | null>(null);

  const features = usePermissionFeatures();
  const permissions = useQuery({
    queryKey: ['person-permissions', personId],
    queryFn: () => api.get<PersonPermissionsDto>(`/api/persons/${personId}/permissions`),
    enabled: mayRead && open,
  });

  const reset = useMutation({
    mutationFn: (feature: Resource) => api.del(`/api/persons/${personId}/permissions/${feature}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['person-permissions', personId] });
      await queryClient.invalidateQueries({ queryKey: ['permissions'] });
      toast.show('Wieder wie Funktion');
    },
    onError: (err: unknown) => toast.show(errorText(err, 'Zurücksetzen fehlgeschlagen'), 'error'),
  });

  if (!mayRead) return null;
  const featureByKey = new Map((features.data ?? []).map((f) => [f.key, f]));
  const exceptionCount = permissions.data?.rows.filter((row) => row.exception).length ?? 0;

  const ausnahmeZuruecksetzen = async (feature: Resource): Promise<void> => {
    const recht = featureByKey.get(feature)?.label ?? feature;
    const ok = await confirm({
      title: 'Ausnahme zurücksetzen?',
      message: `Die Ausnahme für „${recht}“ wird entfernt. Die Person hat dann wieder die Stufe aus ihren Funktionen.`,
      confirmLabel: 'Ja, zurücksetzen',
      tone: 'danger',
    });
    if (ok) reset.mutate(feature);
  };

  return (
    <Card>
      <CardBody className="space-y-3">
        {confirmDialog}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold">Rechte</h2>
            <p className="text-sm text-slate-500">
              Ergeben sich aus den Funktionen. Ausnahmen gelten im gesamten Verein.
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Ausblenden' : 'Rechte anzeigen'}
          </Button>
        </div>

        {open && permissions.isLoading && <LoadingBlock />}
        {open && permissions.error && <ErrorBlock error={permissions.error} />}
        {open && permissions.data && (
          <>
            {permissions.data.isAdmin && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Systemverantwortung: Vollzugriff auf alles, Ausnahmen wirken nicht.
              </p>
            )}
            {exceptionCount > 0 && (
              <p className="text-xs text-slate-500">{exceptionCount} Ausnahme(n) gesetzt.</p>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
                    <th className="py-2 pr-3">Recht</th>
                    <th className="px-2 py-2">Aus Funktionen</th>
                    <th className="px-2 py-2">Ausnahme</th>
                    <th className="px-2 py-2">Wirksam</th>
                    {mayManage && <th className="px-2 py-2" />}
                  </tr>
                </thead>
                <tbody>
                  {permissions.data.rows.map((row) => {
                    const feature = featureByKey.get(row.feature);
                    return (
                      <tr key={row.feature} className={cn('border-b border-slate-100', row.exception && 'bg-amber-50/40')}>
                        <td className="py-2 pr-3 font-medium text-slate-700">
                          {feature ? <FeatureName feature={feature} /> : row.feature}
                        </td>
                        <td className="px-2 py-2">
                          <LevelBadge
                            level={row.roleLevel}
                            title={row.roleSources.length > 0 ? row.roleSources.join('\n') : 'Keine Funktion gibt dieses Recht'}
                          />
                        </td>
                        <td className="px-2 py-2">
                          {row.exception ? (
                            <span className="inline-flex flex-wrap items-center gap-1" title={row.exception.note ?? undefined}>
                              {row.exception.level === 'none' ? <Badge tone="danger">Entzogen</Badge> : <LevelBadge level={row.exception.level} />}
                              {row.exception.validUntil && (
                                <span className="text-xs text-slate-500">bis {formatDate(row.exception.validUntil)}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">wie Funktion</span>
                          )}
                        </td>
                        <td className="px-2 py-2"><LevelBadge level={row.effectiveLevel} /></td>
                        {mayManage && (
                          <td className="whitespace-nowrap px-2 py-2 text-right text-xs">
                            <button
                              type="button"
                              className="text-brand-700 hover:underline"
                              onClick={() => setEditing({ feature: row.feature, current: row.exception })}
                            >
                              {row.exception ? 'ändern' : 'Ausnahme'}
                            </button>
                            {row.exception && (
                              <button
                                type="button"
                                className="ml-3 text-slate-500 hover:text-rose-600"
                                onClick={() => void ausnahmeZuruecksetzen(row.feature)}
                              >
                                zurücksetzen
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {permissions.data.rows.every((row) => row.effectiveLevel === 'none') && (
              <EmptyState title="Keine Rechte" description="Diese Person hat weder eine Funktion mit Rechten noch eine Ausnahme." />
            )}
          </>
        )}

        {editing && (
          <ExceptionDialog
            key={`${editing.feature}-${editing.current?.id ?? 'neu'}`}
            open
            onClose={() => setEditing(null)}
            features={features.data ?? []}
            personId={personId}
            feature={editing.feature}
            current={editing.current}
          />
        )}
      </CardBody>
    </Card>
  );
}
